// Loader
// Features:
// - Custom loading screen overlay
// - Animated progress bar with easing
// - Transitions from "Loading" -> "Start" button -> "Intro" phase

import { useProgress } from '@react-three/drei'
import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { usePhase, PHASES } from '../../store/usePhase'
import './loader.css'

const LOADING_CONFIG = {
    width: 520,
    frame: 2,
    barHeight: 20,
    labelGap: 6,
    animSpeed: 0.18,
    minDiff: 0.2,
}

export default function Loader() {
    const { active, progress, loaded, total } = useProgress()
    const [dots, setDots] = useState(' .')
    const [displayed, setDisplayed] = useState(0)

    const phase = usePhase((state) => state.phase)
    const setPhase = usePhase((state) => state.setPhase)

    const rafRef = useRef(0)
    const lastPctRef = useRef(0)

    // 1. Dot Animation
    useEffect(() => {
        const id = setInterval(() => {
            setDots((prev) => {
                const count = prev.trim().length
                return ' ' + '.'.repeat(count >= 3 ? 1 : count + 1)
            })
        }, 400)
        return () => clearInterval(id)
    }, [])

    // 2. Target Calculation
    const target = useMemo(() => {
        if (Number.isFinite(total) && total > 0) {
            return Math.min(100, Math.max(0, (loaded / total) * 100))
        }
        return active ? Math.max(1, progress) : progress
    }, [active, loaded, total, progress])

    // 3. Smooth Easing
    useEffect(() => {
        const tick = () => {
            setDisplayed((prev) => {
                const diff = target - prev
                if (Math.abs(diff) < LOADING_CONFIG.minDiff) return target
                return prev + diff * LOADING_CONFIG.animSpeed
            })
            rafRef.current = requestAnimationFrame(tick)
        }
        rafRef.current = requestAnimationFrame(tick)
        return () => cancelAnimationFrame(rafRef.current)
    }, [target])

    // 4. Monotonic Progress
    const percent = useMemo(() => {
        const raw = Math.max(0, Math.min(100, Math.round(displayed)))
        if (raw < lastPctRef.current) return lastPctRef.current
        lastPctRef.current = raw
        return raw
    }, [displayed])

    // 5. Phase Transition: Loading -> Warmup
    useEffect(() => {
        if (phase === PHASES.loading && !active && percent >= 100) {
            setPhase(PHASES.warmup)
        }
    }, [phase, active, percent, setPhase])

    const onStart = useCallback(() => setPhase(PHASES.introTravel), [setPhase])

    // Visibility Logic
    const showLoading = phase === PHASES.loading
    const showStart = phase === PHASES.warmup
    if (!showLoading && !showStart) return null

    const cssPhase = showLoading ? 'loading' : 'warmup'

    return (
        <div
            className="uo-root"
            style={{
                '--w': `${LOADING_CONFIG.width}px`,
                '--frame': `${LOADING_CONFIG.frame}px`,
                '--bar-h': `${LOADING_CONFIG.barHeight}px`,
                '--label-gap': `${LOADING_CONFIG.labelGap}px`,
            }}
            data-css-phase={cssPhase}
        >
            <div className="uo-stack">
                {/* LOADING PANEL */}
                <div className="uo-panel uo-loading" aria-hidden={!showLoading}>
                    <div className="uo-center">
                        <div className="lf-labels">
                            <div className="lf-left">LOADING{dots}</div>
                            <div className="lf-right">{percent}%</div>
                        </div>
                        <div className="lf-frame">
                            <div className="lf-inner">
                                <div className="lf-bar">
                                    <div
                                        className="lf-fill"
                                        style={{ width: `${percent}%` }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* START PANEL */}
                <div className="uo-panel uo-start" aria-hidden={!showStart}>
                    <div className="uo-center">
                        <button className="so-button-dom" onClick={onStart}>
                            START
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}
